import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  FaTelegram, 
  FaRoute, 
  FaShieldAlt, 
  FaLeaf,
  FaBolt,
  FaChartLine,
  FaUsers,
  FaCreditCard,
  FaMapMarkerAlt,
  FaClock,
  FaStar,
  FaCheckCircle,
  FaCar
} from 'react-icons/fa';

const Features = () => {
  const [activeTab, setActiveTab] = useState('drivers');

  const coreFeatures = [
    {
      icon: <FaCreditCard className="h-12 w-12" />,
      title: "Zero Platform Fees",
      description: "We charge zero fees for our community service. Direct payment between riders and drivers with no middleman.",
      features: ["No platform fees", "Direct payments", "100% driver earnings", "No hidden charges"]
    },
    {
      icon: <FaUsers className="h-12 w-12" />,
      title: "Fair Pricing System",
      description: "Riders set their own prices, drivers choose which rides to accept. Simple, fair, and transparent.",
      features: ["Rider sets price", "Driver chooses rides", "No surge pricing", "Fair negotiation"]
    },
    {
      icon: <FaTelegram className="h-12 w-12" />,
      title: "Simple Telegram Bots",
      description: "Easy-to-use Telegram bots with natural language processing. No complex apps, just simple messaging interface.",
      features: ["Natural language", "Voice commands", "Simple interface", "No app downloads"]
    },
    {
      icon: <FaLeaf className="h-12 w-12" />,
      title: "Community Service Mission",
      description: "Built by iautenticai as a community service. Technology that serves people, not corporate profits.",
      features: ["Non-profit focused", "Community driven", "Open platform", "No restrictions"]
    }
  ];

  const driverFeatures = [
    {
      icon: <FaCreditCard className="h-8 w-8" />,
      title: "Keep 100% of Earnings",
      description: "You keep every penny you earn. Direct payment from riders with zero platform fees."
    },
    {
      icon: <FaUsers className="h-8 w-8" />,
      title: "Choose Your Rides",
      description: "See rider offers upfront and accept only the rides you want. Complete freedom to choose."
    },
    {
      icon: <FaShieldAlt className="h-8 w-8" />,
      title: "Fair and Open Platform",
      description: "Simple, straightforward service with no complex requirements or restrictions."
    },
    {
      icon: <FaChartLine className="h-8 w-8" />,
      title: "Full Earnings Transparency",
      description: "Track your true earnings without corporate cuts. Simple, honest payment system."
    }
  ];

  const riderFeatures = [
    {
      icon: <FaCreditCard className="h-8 w-8" />,
      title: "You Set the Price",
      description: "Decide what you want to pay for your ride. Simple, transparent pricing that you control."
    },
    {
      icon: <FaClock className="h-8 w-8" />,
      title: "No Surge Pricing",
      description: "Pay what you think is fair, always. No sudden price increases during busy times."
    },
    {
      icon: <FaUsers className="h-8 w-8" />,
      title: "Direct Driver Connection",
      description: "Connect directly with drivers for real human-to-human interaction and communication."
    },
    {
      icon: <FaBolt className="h-8 w-8" />,
      title: "Simple Telegram Interface",
      description: "No complex apps to download. Just message our bot on Telegram - it's that simple."
    }
  ];

  const techFeatures = [
    {
      title: "AI-Powered Matching",
      description: "Advanced machine learning algorithms for optimal ride matching",
      stats: "99.2% match accuracy"
    },
    {
      title: "Real-time Processing",
      description: "Instant ride matching and updates with sub-second response times",
      stats: "<500ms response"
    },
    {
      title: "Scalable Infrastructure",
      description: "Cloud-native architecture supporting thousands of concurrent users",
      stats: "99.9% uptime"
    },
    {
      title: "Smart Routing",
      description: "Intelligent route optimization considering traffic and preferences",
      stats: "25% time savings"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Boulevard Features - Community Ride Sharing</title>
        <meta name="description" content="Boulevard features: 0% platform fees, fair pricing, Telegram bot integration. Simple community ride sharing service." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Simple Community Ride Sharing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Boulevard connects riders and drivers directly through simple Telegram bots. 
              No platform fees, fair pricing, built as a community service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Core Features
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need for seamless ride-sharing
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {coreFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card p-8"
              >
                <div className="text-primary-600 mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.features.map((item, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <FaCheckCircle className="text-secondary-500 text-sm" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* User-Specific Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Tailored for Every User
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Specialized features designed for drivers and riders
            </p>

            {/* Tab Navigation */}
            <div className="flex justify-center mb-12">
              <div className="bg-white rounded-lg p-1 shadow-lg">
                <button
                  onClick={() => setActiveTab('drivers')}
                  className={`px-6 py-3 rounded-md font-semibold transition-all duration-200 ${
                    activeTab === 'drivers'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:text-primary-600'
                  }`}
                >
                  For Drivers
                </button>
                <button
                  onClick={() => setActiveTab('riders')}
                  className={`px-6 py-3 rounded-md font-semibold transition-all duration-200 ${
                    activeTab === 'riders'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:text-primary-600'
                  }`}
                >
                  For Riders
                </button>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {(activeTab === 'drivers' ? driverFeatures : riderFeatures).map((feature, index) => (
                <div key={index} className="card p-6">
                  <div className="text-primary-600 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Technical Excellence
            </h2>
            <p className="text-xl text-gray-600">
              Built on cutting-edge technology for superior performance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {techFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-primary-600 rounded-full"></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-3">
                  {feature.description}
                </p>
                <div className="text-primary-600 font-bold text-lg">
                  {feature.stats}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Benefits */}
      <section className="py-20 bg-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Community Benefits
            </h2>
            <p className="text-xl text-gray-600">
              How Boulevard serves the community with fair, transparent ride sharing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-green-600 mb-4">💰 Zero Platform Fees</h3>
              <p className="text-gray-700">
                Drivers keep 100% of their earnings. No hidden fees or commissions taken from either party.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-blue-600 mb-4">🤝 Fair Pricing</h3>
              <p className="text-gray-700">
                Riders set their own prices, drivers choose what works for them. No surge pricing or price manipulation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-purple-600 mb-4">📱 Simple Interface</h3>
              <p className="text-gray-700">
                Easy-to-use Telegram bots with natural language processing. No complex apps to download.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-orange-600 mb-4">🌍 Community Service</h3>
              <p className="text-gray-700">
                Built as a community service by iautenticai. Technology that serves people, not profit.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-red-600 mb-4">� Privacy First</h3>
              <p className="text-gray-700">
                Your data stays private. No tracking, no selling of personal information to third parties.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-teal-600 mb-4">⚡ Fast & Reliable</h3>
              <p className="text-gray-700">
                Quick matching system with real-time updates and reliable service availability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              Security & Privacy First
            </h2>
            <p className="text-xl text-gray-300">
              Your safety and privacy are our top priorities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "End-to-End Encryption",
                description: "All communications and data transfers are protected with military-grade encryption."
              },
              {
                title: "Identity Verification",
                description: "Multi-step verification process ensures all users are authentic and trustworthy."
              },
              {
                title: "Privacy Protection",
                description: "Your personal data is never shared without consent and is stored securely."
              },
              {
                title: "Real-time Monitoring",
                description: "24/7 system monitoring to detect and prevent any suspicious activities."
              },
              {
                title: "Emergency Features",
                description: "Quick access to emergency contacts and authorities when needed."
              },
              {
                title: "Secure Payments",
                description: "PCI-compliant payment processing with fraud detection and prevention."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-800 p-6 rounded-xl"
              >
                <h3 className="text-xl font-semibold mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-300">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
              Experience the Future of Transportation
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Join thousands of users who have already discovered the power of smart ride-sharing. 
              Try Boulevard today and see the difference.
            </p>
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 justify-center">
              <a
                href="https://t.me/Rideatease_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white hover:bg-blue-700 font-semibold py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 inline-flex items-center justify-center space-x-3"
              >
                <FaTelegram />
                <span>Rider Bot </span>
              </a>
              <a
                href="https://t.me/Rideeasedriver_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 text-white hover:bg-green-700 font-semibold py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 inline-flex items-center justify-center space-x-3"
              >
                <FaTelegram />
                <span>Driver Bot </span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Features;
